// eslint-disable-next-line jira/filename-case
import React, { useContext, type ReactElement, useState } from 'react';
import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';
import { fg } from '@atlassian/jira-feature-gating';
import { WaitContext } from '@atlassian/react-loosely-lazy/lazy-wait/context';
import { LazySuspense } from '@atlassian/react-loosely-lazy/suspense/component';
import useLayoutEffectSAFE from '@jira/platform__app-framework-use-layout-effect-safe/src/useLayoutEffectSafe.tsx';
import { useOwnershipMarkerComponent } from '@jira/platform__ownership-marker/src/ui/useOwnershipMarkerComponent.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import type { PlaceholderProps } from './PlaceholderProps.tsx';
import { PlaceholderSsrFallback } from './PlaceholderSsrFallback.tsx';
import { SuspensePlaceholder } from './SuspensePlaceholder.tsx';

function Hold({ name }: { name?: string }): null {
	const interactionContext = useContext(UFOInteractionContext);
	const waitContext = useContext(WaitContext);
	const nameWithUnderscores = name?.replaceAll('/', '_');

	// Runs synchronously, to allow us to identify async items
	useLayoutEffectSAFE(() => {
		// if until context exists means there is a LazyWait parent component
		// we use that value
		const shouldHold = waitContext ? !!waitContext.currentValue() : true;
		if (interactionContext != null && shouldHold) {
			return interactionContext.hold(nameWithUnderscores);
		}
	}, [interactionContext, nameWithUnderscores, waitContext]);
	return null;
}

/**
 * This is a "safe-to-use" Placeholder but it's actually expected to be used only around React-Loosely-Lazy components.
 * WARNING: Make sure we use same placeholders in a rendering tree.
 * Mixed use may cause unexpected behavior, such as rendering child component twice.
 * Ref: https://hello.atlassian.net/wiki/x/iJpOHwE
 */
function RLLPlaceholder({
	name,
	children,
	fallback,
	reportSsrFallback,
}: PlaceholderProps): ReactElement {
	// react-18: can use fallback directly
	const fb = fallback != null ? fallback : null;
	// react-18: can use children directly
	const kids = children != null ? children : null;
	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });
	return (
		// oxlint-disable-next-line react/static-components
		<OwnershipMarker>
			<LazySuspense
				fallback={
					<>
						{fb}
						{reportSsrFallback && fg('jfp_magma_placeholder_ssr_fallback_reporting') && (
							<PlaceholderSsrFallback name={name} reportSsrFallback={reportSsrFallback} />
						)}
						<Hold name={name} />
					</>
				}
				name={name}
			>
				{kids}
			</LazySuspense>
		</OwnershipMarker>
	);
}

const Placeholder: React.FC<PlaceholderProps> = componentWithCondition(
	// this condition is coupled with hydration rollout, not the fact of hydration
	// SuspensePlaceholder can be used for non-hydrating pages as well, if no "SSR content preservation" is required
	// See PlaceholderWithNativeSuspense
	() => useIsHydrateRenderMode(),
	SuspensePlaceholder,
	RLLPlaceholder,
);
export default Placeholder;

export const PlaceholderWithNativeSuspense: React.FC<PlaceholderProps> = SuspensePlaceholder;

/**
 * Conditionally renders a PlaceholderWithNativeSuspense.
 * `condition` is expected to not change, as changing it would trigger a remount
 * For that reason it is frozen to its initial value it had
 */
export function ConditionalPlaceholderWithNativeSuspense({
	name,
	condition,
	children,
	fallback,
	reportSsrFallback,
}: React.PropsWithChildren<{
	name: string;
	/** WARNING: This condition is frozen as changing it would cause a remount. Only use fg/expVal! */
	condition: boolean;
	fallback: React.ReactNode;
	reportSsrFallback?: PlaceholderProps['reportSsrFallback'];
}>): React.JSX.Element {
	const [frozenCondition] = useState(condition);
	if (frozenCondition) {
		return (
			<PlaceholderWithNativeSuspense
				name={name}
				fallback={fallback}
				reportSsrFallback={reportSsrFallback}
			>
				{children}
			</PlaceholderWithNativeSuspense>
		);
	}
	return <>{children}</>;
}

/**
 * A `ConditionalPlaceholderWithNativeSuspense` present in the tree only with the intention of reducing
 * the blast radius of hydration mismatches in this section of it, to prevent large parts of the
 * server-rendered DOM from being removed and replaced by client-rendered content.
 *
 * Hydration boundaries are expected to be removed at a later point, once the underlying hydration errors are resolved or minimised.
 */
export const ConditionalHydrationBoundaryPlaceholder: typeof ConditionalPlaceholderWithNativeSuspense =
	ConditionalPlaceholderWithNativeSuspense;
